import { useState } from 'react';
import { z } from 'zod';
import {
  crmAdviserDirectorySchema,
  adviserCrmLinkSchema,
  type Adviser,
  type BotScope,
} from '@cryptology-assist/domain';
import { request, savedSchema, type Session } from './api';
import { useRemote } from './hooks';
import { ErrorNotice, Field, onSubmit, useMutation } from './components';
import type { Copy } from './copy';
export const accountBotsSchema = z.object({
  bots: z.array(z.object({ id: z.string(), name: z.string() })),
});
const linksSchema = z.object({ links: z.array(adviserCrmLinkSchema) });
const connectionsSchema = z.object({
  connections: z.array(
    z.object({
      id: z.string(),
      provider: z.string(),
      endpoint: z.string(),
      status: z.string(),
      capabilities: z.array(z.string()),
    })
  ),
});
export function AdviserCrm({
  scope,
  person,
  session,
  copy,
}: {
  scope: BotScope;
  person: Adviser;
  session: Session;
  copy: Copy;
}) {
  const base = `/v1/accounts/${scope.companyId}`;
  const bots = useRemote(`${base}/bots`, accountBotsSchema);
  const links = useRemote(
    `${base}/advisers/${person.id}/crm-links`,
    linksSchema
  );
  const [botId, setBotId] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [after, setAfter] = useState<string | null>(null);
  const [employeeId, setEmployeeId] = useState('');
  const connections = useRemote(
    botId ? `${base}/business-connections?botId=${botId}` : null,
    connectionsSchema
  );
  const directory = useRemote(
    botId && connectionId
      ? `${base}/crm-advisers?botId=${botId}&connectionId=${connectionId}${after ? `&after=${after}` : ''}`
      : null,
    crmAdviserDirectorySchema
  );
  const mutation = useMutation();
  return (
    <section className="form-section">
      <h2>{copy.crmAdviserLinks}</h2>
      <p>{copy.crmAdviserLinksHelp}</p>
      <ErrorNotice
        error={
          bots.error ??
          links.error ??
          connections.error ??
          directory.error ??
          mutation.error
        }
        copy={copy}
        onRetry={() => {
          bots.reload();
          links.reload();
          connections.reload();
          directory.reload();
        }}
      />
      {links.data?.links.map((link) => (
        <CrmIdentity
          key={`${link.botId}/${link.businessConnectionId}`}
          base={base}
          link={link}
          assistantName={
            bots.data?.bots.find((bot) => bot.id === link.botId)?.name
          }
          copy={copy}
          busy={mutation.busy}
          unlink={() =>
            void mutation.run(async () => {
              await request(
                `${base}/advisers/${person.id}/crm-links`,
                savedSchema,
                {
                  method: 'DELETE',
                  csrf: session.csrf,
                  body: {
                    botId: link.botId,
                    businessConnectionId: link.businessConnectionId,
                  },
                }
              );
              links.reload();
            })
          }
        />
      ))}
      <form
        className="edit-panel"
        onSubmit={(event) =>
          onSubmit(event, () =>
            mutation.run(async () => {
              if (!directory.data)
                throw new Error('CRM_ADVISER_DIRECTORY_UNAVAILABLE');
              await request(
                `${base}/advisers/${person.id}/crm-links`,
                savedSchema,
                {
                  csrf: session.csrf,
                  body: {
                    botId,
                    businessConnectionId: connectionId,
                    crmCompanyId: directory.data.crmCompanyId,
                    employeeId,
                  },
                }
              );
              setEmployeeId('');
              links.reload();
            })
          )
        }
      >
        <Field label={copy.selectBot}>
          <select
            required
            value={botId}
            onChange={(event) => {
              setBotId(event.target.value);
              setConnectionId('');
              setEmployeeId('');
              setAfter(null);
            }}
          >
            <option value="">{copy.selectBot}</option>
            {bots.data?.bots
              .filter((bot) => person.botIds.includes(bot.id))
              .map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name}
                </option>
              ))}
          </select>
        </Field>
        {botId && (
          <Field label={copy.crmSelectConnection}>
            <select
              required
              value={connectionId}
              onChange={(event) => {
                setConnectionId(event.target.value);
                setEmployeeId('');
                setAfter(null);
              }}
            >
              <option value="">{copy.crmSelectConnection}</option>
              {connections.data?.connections
                .filter((connection) => connection.status === 'active')
                .map((connection) => (
                  <option key={connection.id} value={connection.id}>
                    {connection.provider} ·{' '}
                    {new URL(connection.endpoint).hostname}
                  </option>
                ))}
            </select>
          </Field>
        )}
        {connections.data?.connections.length === 0 && (
          <p>{copy.crmNoConnections}</p>
        )}
        {directory.loading && <p>{copy.loading}</p>}
        {directory.data && (
          <>
            <Field label={copy.crmPerson}>
              <select
                required
                value={employeeId}
                onChange={(event) => setEmployeeId(event.target.value)}
              >
                <option value="">{copy.selectAdviser}</option>
                {directory.data.advisers.map((adviser) => (
                  <option key={adviser.id} value={adviser.id}>
                    {adviser.name}
                    {adviser.email ? ` · ${adviser.email}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <div className="form-actions">
              {after && (
                <button
                  type="button"
                  onClick={() => {
                    setAfter(null);
                    setEmployeeId('');
                  }}
                >
                  {copy.firstPage}
                </button>
              )}
              {directory.data.nextCursor && (
                <button
                  type="button"
                  onClick={() => {
                    setAfter(directory.data!.nextCursor);
                    setEmployeeId('');
                  }}
                >
                  {copy.nextPage}
                </button>
              )}
            </div>
          </>
        )}
        <button className="primary" disabled={mutation.busy || !employeeId}>
          {copy.linkCrmAdviser}
        </button>
      </form>
    </section>
  );
}

function CrmIdentity({
  base,
  link,
  assistantName,
  copy,
  busy,
  unlink,
}: {
  base: string;
  link: z.infer<typeof adviserCrmLinkSchema>;
  assistantName: string | undefined;
  copy: Copy;
  busy: boolean;
  unlink: () => void;
}) {
  const directory = useRemote(
    `${base}/crm-advisers?botId=${link.botId}&connectionId=${link.businessConnectionId}&employeeId=${link.employeeId}`,
    crmAdviserDirectorySchema
  );
  const person =
    directory.data?.crmCompanyId === link.crmCompanyId
      ? directory.data.advisers.find((person) => person.id === link.employeeId)
      : undefined;
  return (
    <div className="connection-row">
      <div>
        <strong>{assistantName}</strong>
        {directory.loading && !directory.data && <p>{copy.loading}</p>}
        <ErrorNotice
          error={directory.error}
          copy={copy}
          onRetry={directory.reload}
        />
        {person ? (
          <p>
            {copy.crmLinked} · {person.name}
            {person.email ? ` · ${person.email}` : ''}
          </p>
        ) : (
          directory.data && <p role="alert">{copy.CRM_ADVISER_NOT_ELIGIBLE}</p>
        )}
      </div>
      <button disabled={busy} onClick={unlink}>
        {copy.unlinkCrmAdviser}
      </button>
    </div>
  );
}
